import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import { useDownloadOrbitLContractMutation } from "Api/Mutations/Merchants/useDownloadOrbitLContractMutation";
import { useSubmitOrbitLOrderMutation } from "Api/Mutations/Merchants/useSubmitOrbitLOrderMutation";
import { useState } from "react";
import { format } from "date-fns";
import { OrbitLContractType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Components.Merchant.OrbitL.Contract;

type OrbitLContractConfirmationProps = {
  merchantPublicID: string;
  contractDownloadedAt?: string | null;
  paymentServicesDownloadedAt?: string | null;
  // SoftPOS-only contracts lend no device, so the loan agreement is skipped.
  isLoanContractRequired: boolean;
};

export const OrbitLContractConfirmation = ({
  merchantPublicID,
  contractDownloadedAt,
  paymentServicesDownloadedAt,
  isLoanContractRequired,
}: OrbitLContractConfirmationProps) => {
  const { t } = useResource();
  const [isContractAccepted, setIsContractAccepted] = useState(false);
  const loanDownloadMutation = useDownloadOrbitLContractMutation({
    onSuccess: () => toast.success(t(RESOURCES.DownloadSuccess)),
    onError: () => toast.error(t(RESOURCES.DownloadError)),
  });
  const paymentServicesDownloadMutation = useDownloadOrbitLContractMutation({
    onSuccess: () => toast.success(t(RESOURCES.DownloadSuccess)),
    onError: () => toast.error(t(RESOURCES.DownloadError)),
  });
  const submitMutation = useSubmitOrbitLOrderMutation({
    onSuccess: () => toast.success(t(RESOURCES.SubmitSuccess)),
    onError: () => toast.error(t(RESOURCES.SubmitErrorGeneric)),
  });

  const handleDownloadLoan = () => {
    loanDownloadMutation.mutate({
      merchantPublicID,
      contractType: OrbitLContractType.Loan,
      fileNamePrefix: t(RESOURCES.LoanFileName),
    });
  };

  const handleDownloadPaymentServices = () => {
    paymentServicesDownloadMutation.mutate({
      merchantPublicID,
      contractType: OrbitLContractType.PaymentServices,
      fileNamePrefix: t(RESOURCES.PaymentServicesFileName),
    });
  };

  const handleSubmit = () => {
    if (!isContractAccepted) {
      return;
    }
    submitMutation.mutate({ merchantPublicID });
  };

  const isLoanDownloaded = !!contractDownloadedAt;
  const isPaymentServicesDownloaded = !!paymentServicesDownloadedAt;
  const areRequiredDownloaded =
    isPaymentServicesDownloaded &&
    (isLoanDownloaded || !isLoanContractRequired);

  return (
    <Stack gap={3}>
      {/* Contract 1: Payment services agreement */}
      <Stack gap={2}>
        <Typography variant="h6">
          {t(RESOURCES.PaymentServicesTitle)}
        </Typography>

        {isPaymentServicesDownloaded && (
          <Alert severity="info">
            {t(RESOURCES.DownloadedAt, {
              date: format(
                new Date(paymentServicesDownloadedAt),
                "dd.MM.yyyy HH:mm",
              ),
            })}
          </Alert>
        )}

        <Button
          variant={isPaymentServicesDownloaded ? "outlined" : "contained"}
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPaymentServices}
          disabled={paymentServicesDownloadMutation.isPending}
        >
          {isPaymentServicesDownloaded
            ? t(RESOURCES.DownloadAgainButton)
            : t(RESOURCES.DownloadButton)}
        </Button>
      </Stack>

      {isLoanContractRequired && (
        <>
          <Divider />

          {/* Contract 2: Terminal loan agreement */}
          <Stack gap={2}>
            <Typography variant="h6">{t(RESOURCES.LoanTitle)}</Typography>

            {isLoanDownloaded && (
              <Alert severity="info">
                {t(RESOURCES.DownloadedAt, {
                  date: format(
                    new Date(contractDownloadedAt),
                    "dd.MM.yyyy HH:mm",
                  ),
                })}
              </Alert>
            )}

            <Button
              variant={isLoanDownloaded ? "outlined" : "contained"}
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadLoan}
              disabled={loanDownloadMutation.isPending}
            >
              {isLoanDownloaded
                ? t(RESOURCES.DownloadAgainButton)
                : t(RESOURCES.DownloadButton)}
            </Button>
          </Stack>
        </>
      )}

      {/* Acceptance checkbox + submit — only after every required contract is downloaded */}
      {areRequiredDownloaded && (
        <>
          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={isContractAccepted}
                onChange={(e) => setIsContractAccepted(e.target.checked)}
              />
            }
            label={<Typography>{t(RESOURCES.AcceptanceLabel)}</Typography>}
          />

          <Button
            variant="contained"
            color="success"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={!isContractAccepted || submitMutation.isPending}
          >
            {submitMutation.isPending
              ? t(RESOURCES.SubmittingButton)
              : t(RESOURCES.SubmitButton)}
          </Button>

          <Typography variant="caption" color="text.secondary">
            {t(RESOURCES.SubmitNote)}
          </Typography>
        </>
      )}
    </Stack>
  );
};
